import { Contactpersoon, useDeleteContactpersoon } from "@/entities/contactpersoon";
import { Medewerker, Mol, usePatchMedewerkersOpStation, Vos } from "@/entities/medewerkers-op-station";
import { RoleGate, UserRole } from "@/features/auth";
import { NieuwSmsBerichtDialog } from "@/features/berichten/components/nieuw-sms-bericht-dialog";
import { ActionsDropdown, Badge, Button, ConfirmDialog, DropdownMenuItem, Icon, Separated } from "@/shared/components";
import { ReactNode, useState } from "react";
import { useParams } from "react-router";

type MedewerkersProps = {
    vossen: Vos[];
    mollen: Mol[];
};

export function Medewerkers({ vossen, mollen }: MedewerkersProps) {
    return (
        <ul className="bg-elevated-sunken">
            <Separated>
                {vossen.map((vos) => (
                    <VosRow key={vos.contactpersoon.id} vos={vos} />
                ))}
                {mollen.map((mol) => (
                    <MolRow key={mol.contactpersoon.id} mol={mol} />
                ))}
            </Separated>
        </ul>
    );
}

type VosRowProps = {
    vos: Vos;
};

function VosRow({ vos }: VosRowProps) {
    return (
        <MedewerkerRow
            medewerker={vos}
            type="VOS"
            statusBadge={
                <Badge
                    variant={vos.status === "Geaccepteerd" ? "green-subtle" : "red-subtle"}
                    className="flex gap-1"
                    data-testid="vos-status-badge"
                >
                    <Icon name={vos.status === "Geaccepteerd" ? "check-circle" : "stop"} size="xs" />
                    {vos.status}
                </Badge>
            }
        />
    );
}

type MolRowProps = {
    mol: Mol;
};

function MolRow({ mol }: MolRowProps) {
    return <MedewerkerRow medewerker={mol} type="MOL" />;
}

type MedewerkerRowProps = {
    medewerker: Vos | Mol;
    type: "VOS" | "MOL";
    statusBadge?: ReactNode;
};

function MedewerkerRow({ medewerker, type, statusBadge }: MedewerkerRowProps) {
    const { id: dossierNummer } = useParams() as { id: string };
    const [showNieuwSmsBerichtDialog, setShowNieuwSmsBerichtDialog] = useState(false);

    return (
        <li
            className="text-s flex items-center justify-between py-1.5 pr-4 pl-10"
            data-testid={`vos-medewerker-${medewerker.contactpersoon.telefoonnummer}`}
        >
            <MedewerkerContactInfo contactpersoon={medewerker.contactpersoon} />
            <div className="flex items-center gap-1.5">
                <div className="flex gap-2">
                    <Badge variant={"gray-subtle"} data-testid="vos-or-mol-badge">
                        {type}
                    </Badge>
                    {statusBadge}
                </div>
                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                    <Button
                        iconOnly
                        icon="comment-alt-add"
                        iconSize="xs"
                        variant="tertiary"
                        className="mr-2"
                        title="Bericht versturen"
                        onClick={() => setShowNieuwSmsBerichtDialog(true)}
                    />
                    <MedewerkerActions medewerker={medewerker} dossierNummer={dossierNummer} />
                    {showNieuwSmsBerichtDialog && (
                        <NieuwSmsBerichtDialog
                            dossierId={dossierNummer}
                            open={showNieuwSmsBerichtDialog}
                            onOpenChange={setShowNieuwSmsBerichtDialog}
                            prefilledContactIds={[String(medewerker.contactpersoon.id)]}
                            allowCreateContactpersoon={false}
                        />
                    )}
                </RoleGate>
            </div>
        </li>
    );
}

type MedewerkerContactInfoProps = {
    contactpersoon: Contactpersoon;
};

function MedewerkerContactInfo({ contactpersoon }: MedewerkerContactInfoProps) {
    return (
        <div className="grid">
            <span className="text-content-emphasis font-medium">{contactpersoon.naam ?? "Onbekende MOL"}</span>
            <span className="text-content-subtle">{contactpersoon.telefoonnummer}</span>
        </div>
    );
}

type MedewerkerActionsProps = {
    dossierNummer: string;
    medewerker: Vos | Mol;
};

function MedewerkerActions({ medewerker, dossierNummer }: MedewerkerActionsProps) {
    const patchMedewerkersOpStation = usePatchMedewerkersOpStation();
    const deleteContactpersoon = useDeleteContactpersoon();

    const [stopVosInzetDialogOpen, setStopVosInzetDialogOpen] = useState(false);
    const [deleteMolDialogOpen, setDeleteMolDialogOpen] = useState(false);

    const handleStopInzet = () => {
        patchMedewerkersOpStation.mutate(
            {
                dossierNummer,
                actie: "Stop",
                scope: "Vos",
                contactpersoonId: medewerker.contactpersoon.id,
            },
            {
                onSuccess: () => {
                    setStopVosInzetDialogOpen(false);
                },
            }
        );
    };

    const handleDeleteMol = () => {
        if (Medewerker.isVos(medewerker)) return;

        deleteContactpersoon.mutate(
            {
                dossierId: dossierNummer,
                contactpersoonId: medewerker.contactpersoon.id,
            },
            {
                onSuccess: () => {
                    setDeleteMolDialogOpen(false);
                },
            }
        );
    };

    const allowStopVos = Medewerker.isVos(medewerker) && medewerker.status === "Geaccepteerd";
    const allowRemoveMol = !Medewerker.isVos(medewerker);
    const molLabel =
        medewerker.contactpersoon.naam ??
        `met telefoonnummer ${medewerker.contactpersoon.telefoonnummer} op ${medewerker.contactpersoon.stationcode}`;

    if (!allowStopVos && !allowRemoveMol) return <div className="w-6"></div>; // alignment hack for missing menu

    return (
        <>
            <ActionsDropdown>
                {allowStopVos && <DropdownMenuItem onSelect={() => setStopVosInzetDialogOpen(true)}>Inzet VOS stoppen</DropdownMenuItem>}
                {allowRemoveMol && <DropdownMenuItem onSelect={() => setDeleteMolDialogOpen(true)}>MOL verwijderen</DropdownMenuItem>}
            </ActionsDropdown>
            {stopVosInzetDialogOpen && (
                <ConfirmDialog
                    isOpen={stopVosInzetDialogOpen}
                    onCancel={() => setStopVosInzetDialogOpen(false)}
                    onConfirm={handleStopInzet}
                    isPending={patchMedewerkersOpStation.isPending}
                    error={patchMedewerkersOpStation.error}
                    title="VOS inzet stoppen"
                    confirmLabel="Inzet stopzetten"
                    cancelLabel="Annuleren"
                    destructive
                >
                    Weet je zeker dat je de inzet van deze VOS wilt stopzetten? Hij/zij krijgt een sms-bericht dat inzet niet langer nodig
                    is.
                </ConfirmDialog>
            )}
            {deleteMolDialogOpen && (
                <ConfirmDialog
                    isOpen={deleteMolDialogOpen}
                    onCancel={() => setDeleteMolDialogOpen(false)}
                    onConfirm={handleDeleteMol}
                    isPending={deleteContactpersoon.isPending}
                    error={deleteContactpersoon.error}
                    title="MOL verwijderen"
                    confirmLabel="Verwijderen"
                    cancelLabel="Annuleren"
                    destructive
                >
                    Weet je zeker dat je MOL {molLabel} wilt verwijderen uit dit dossier?
                </ConfirmDialog>
            )}
        </>
    );
}
